import { ErrorMessage, Field, Form, Formik } from "formik";
import { House } from "@phosphor-icons/react";
import { FC } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useVerifyAddress from "../hooks/useVerifyAddress";
import { toast } from "sonner";

const schema = yup.object().shape({
  state: yup
    .string()
    .required("State is required")
    .min(2, "State must be at least 2 characters long"),
  buildingName: yup.string().required("Building name is required."),
  buildingNumber: yup
    .string()
    .matches(/^\d+$/, "Building number must be numeric")
    .optional(),
  landMark: yup.string().optional(),
  street: yup
    .string()
    .required("Street is required")
    .min(2, "Street must be at least 2 characters long"),
  subStreet: yup.string().optional(),
  city: yup
    .string()
    .required("City is required")
    .min(2, "City must be at least 2 characters long"),
  lga: yup
    .string()
    .required("LGA is required")
    .min(2, "LGA must be at least 2 characters long"),
});

const ChangeAddressModal: FC<{
  setShowAddressModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setShowAddressModal }) => {
  const {
    verifyAddress,
    isVerifyingAddressError,
    verifyingAddress,
    verifyingAddressError,
  } = useVerifyAddress({
    onSuccess(data) {
      setShowAddressModal(false);
      toast.success(data.message);
    },
  });
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 py-5 z-50"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          state: "",
          buildingName: "",
          buildingNumber: "",
          flatNumber: "null",
          landMark: "",
          street: "",
          subStreet: "",
          city: "",
          lga: "",
        }}
        onSubmit={(values) => {
          verifyAddress(values);
        }}
      >
        {({ values, handleChange, isValid, dirty }) => {
          return (
            <Form className="bg-white h-full overflow-y-auto max-w-xl w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <House size={28} />
              </div>
              <div className="flex items-center flex-col">
                <div className="font-medium">Verify your Home Address</div>
                <p className="text-gray-500 text-center">Where do you live?</p>
              </div>
              <div className="w-full flex gap-2">
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="buildingNumber"
                    className="text-sm font-bold text-gray-700"
                  >
                    House Number
                  </label>
                  <Field
                    type="text"
                    id="buildingNumber"
                    name="buildingNumber"
                    placeholder="House Number"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  />
                  <ErrorMessage
                    name="buildingNumber"
                    component="div"
                    className="text-red-500"
                  />
                </div>
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="buildingName"
                    className="text-sm font-bold text-gray-700"
                  >
                    Building Name
                  </label>
                  <Field
                    type="text"
                    id="buildingName"
                    name="buildingName"
                    placeholder="Mama Tee's Home"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  />
                  <ErrorMessage
                    name="buildingName"
                    component="div"
                    className="text-red-500"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full flex-1">
                <label
                  htmlFor="street"
                  className="text-sm font-bold text-gray-700"
                >
                  Street
                </label>
                <Field
                  type="text"
                  id="street"
                  name="street"
                  placeholder="Street e.g Balogun Street"
                  className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                />
                <ErrorMessage
                  name="street"
                  component="div"
                  className="text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2 w-full flex-1">
                <label
                  htmlFor="street"
                  className="text-sm font-bold text-gray-700"
                >
                  Street 2
                </label>
                <Field
                  type="text"
                  id="subStreet"
                  name="subStreet"
                  placeholder="Street 2 e.g Lame Street"
                  className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                />
                <ErrorMessage
                  name="subStreet"
                  component="div"
                  className="text-red-500"
                />
              </div>
              <div className="w-full flex gap-2">
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="subStreet"
                    className="text-sm font-bold text-gray-700"
                  >
                    Town
                  </label>
                  <Field
                    type="text"
                    id="landMark"
                    name="landMark"
                    placeholder="Town e.g Ikotun"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  />
                  <ErrorMessage
                    name="landMark"
                    component="div"
                    className="text-red-500"
                  />
                </div>
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="lga"
                    className="text-sm font-bold text-gray-700"
                  >
                    Local Government Area
                  </label>
                  <Field
                    type="text"
                    id="lga"
                    name="lga"
                    placeholder="LGA"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  />
                  <ErrorMessage
                    name="lga"
                    component="div"
                    className="text-red-500"
                  />
                </div>
              </div>

              <div className="flex gap-5 w-full">
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="state"
                    className="text-sm font-bold text-gray-700"
                  >
                    State
                  </label>
                  <select
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    name="state"
                    id="state"
                    value={values.state}
                    onChange={handleChange("state")}
                    defaultValue="Lagos"
                  >
                    <option value="Abuja FCT">Abuja FCT</option>
                    <option value="Abia">Abia</option>
                    <option value="Adamawa">Adamawa</option>
                    <option value="Akwa Ibom">Akwa Ibom</option>
                    <option value="Anambra">Anambra</option>
                    <option value="Bauchi">Bauchi</option>
                    <option value="Bayelsa">Bayelsa</option>
                    <option value="Benue">Benue</option>
                    <option value="Borno">Borno</option>
                    <option value="Cross River">Cross River</option>
                    <option value="Delta">Delta</option>
                    <option value="Ebonyi">Ebonyi</option>
                    <option value="Edo">Edo</option>
                    <option value="Ekiti">Ekiti</option>
                    <option value="Enugu">Enugu</option>
                    <option value="Gombe">Gombe</option>
                    <option value="Imo">Imo</option>
                    <option value="Jigawa">Jigawa</option>
                    <option value="Kaduna">Kaduna</option>
                    <option value="Kano">Kano</option>
                    <option value="Katsina">Katsina</option>
                    <option value="Kebbi">Kebbi</option>
                    <option value="Kogi">Kogi</option>
                    <option value="Kwara">Kwara</option>
                    <option value="Lagos">Lagos</option>
                    <option value="Nassarawa">Nassarawa</option>
                    <option value="Niger">Niger</option>
                    <option value="Ogun">Ogun</option>
                    <option value="Ondo">Ondo</option>
                    <option value="Osun">Osun</option>
                    <option value="Oyo">Oyo</option>
                    <option value="Plateau">Plateau</option>
                    <option value="Rivers">Rivers</option>
                    <option value="Sokoto">Sokoto</option>
                    <option value="Taraba">Taraba</option>
                    <option value="Yobe">Yobe</option>
                    <option value="Zamfara">Zamfara</option>
                    <option value="Outside Nigeria">Outside Nigeria</option>
                  </select>

                  <ErrorMessage
                    name="state"
                    component="div"
                    className="text-red-500"
                  />
                </div>
                <div className="flex flex-col gap-2 w-full flex-1">
                  <label
                    htmlFor="city"
                    className="text-sm font-bold text-gray-700"
                  >
                    City
                  </label>
                  <Field
                    type="text"
                    id="city"
                    name="city"
                    placeholder="City"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  />
                  <ErrorMessage
                    name="city"
                    component="div"
                    className="text-red-500"
                  />
                </div>
              </div>

              {isVerifyingAddressError && (
                <div className="text-red-500 text-left flex w-full">
                  {verifyingAddressError?.message}
                </div>
              )}
              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  disabled={verifyingAddress}
                  aria-label="Cancel"
                  onClick={() => setShowAddressModal(false)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={!isValid || !dirty}
                  type="submit"
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {verifyingAddress ? "Verifying..." : "Proceed"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default ChangeAddressModal;
